<script setup>
import { provide, ref, computed, nextTick, inject, onMounted, onServerPrefetch } from 'vue'
import { useRoute } from 'vue-router'
import { getCategoryList } from '@/api/article.js'
import MainPage from './MainPage.vue'
import SubPage from './SubPage.vue'
import { useHead } from '@unhead/vue'
import { pageview } from '@/utils/gtm'
import { filterSpecial, appendUtmParams } from '@/utils'
import NoticePage from '@/views/Error/404.vue'
import { categoryType } from '@/constants'
import { Head } from '@unhead/vue/components'

const route = useRoute()
// const router = useRouter()
const head = ref({})
const showNotice = ref(false)
const { patch } = useHead(head)
const seoTitleSuffix = inject('seoTitleSuffix')
const layerData = inject('layerData')
const categoryPath = computed(() => {
  const _categoryPath = route?.params?.categoryPath
  if (_categoryPath?.endsWith('-c') || _categoryPath?.endsWith('-v')) {
    return _categoryPath.slice(0, _categoryPath.length - 2)
  }
  return _categoryPath
})
const categoryInfo = ref({})
provide('categoryInfo', categoryInfo)
const isSub = computed(() => categoryInfo.value?.pid !== 0)
const componentName = computed(() => (isSub.value ? SubPage : MainPage))
const canonical = computed(
  () =>
    (categoryInfo.value?.link || categoryInfo.value?.parent?.link) +
    (route.query.page ? `?page=${route.query.page}` : '')
)

const loading = ref(true)
const failed = ref(false)
const fetchInfo = async () => {
  try {
    const res = await getCategoryList({ category_path: categoryPath.value })
    if (res.code !== 0) throw new Error(res.message)
    if (
      route.params?.categoryTitle &&
      filterSpecial(res?.data.title) !== route.params?.categoryTitle
    )
      return (showNotice.value = true)
    categoryInfo.value = res?.data || {}
    categoryInfo.value.is_video = res?.data?.cate_type == categoryType.video
    route.meta.layerData = { screenID: !isSub.value ? 'S002' : 'S003' }
    loading.value = false
    nextTick(() => {
      if (canonical.value)
        // TC-1631: 保留 utm 参数在地址栏
        window.history.replaceState(
          history.state,
          null,
          appendUtmParams(
            new URL(canonical.value)?.pathname +
              (route.query.page ? `?page=${route.query.page}` : '')
          )
        )
      head.value = {
        share_text: categoryInfo.value?.share_text || categoryInfo.value?.parent?.share_text || '',
        link: [
          {
            rel: 'canonical',
            key: 'canonical',
            href: canonical.value
          },
          {
            rel: 'alternate',
            hreflang: 'x-default',
            key: 'alternate-x-default',
            href: canonical.value
          },
          {
            rel: 'alternate',
            hreflang: 'zh-hk',
            key: 'alternate-zh-hk',
            href: canonical.value
          }
        ]
      }
      patch(head.value)
      pageview({
        ...layerData.value,
        page_title: !isSub.value ? 'main_category' : 'sub_category',
        screen_class: !isSub.value
          ? categoryInfo.value.title
          : `${categoryInfo.value.parent.title}/${categoryInfo.value.title}`,
        eventCategory: !isSub.value ? 'main_category' : 'sub_category',
        eventLabel: !isSub.value
          ? categoryInfo.value.title
          : `${categoryInfo.value.parent.title}/${categoryInfo.value.title}`,
        eventID: categoryInfo.value.id || '',
        screenLabel: canonical.value
      })
    })
  } catch (error) {
    failed.value = true
    loading.value = false
    // router.replace('/notice.html')
    showNotice.value = true
  }
}
onMounted(fetchInfo)
onServerPrefetch(fetchInfo)

const seo = inject('seo')
const SeoMeta = computed(() => {
  let title = categoryInfo.value?.seo_title
  let description = categoryInfo.value?.meta_description
  let ogImage = categoryInfo.value?.og_image_url
  let ogImageAlt = categoryInfo.value?.og_image_alt_txt
  //只有文章分类才用父级分类的seo描述
  if (categoryInfo.value.is_video) {
    title = title || seo.value?.seo_title || ''
    description = description || seo.value?.meta_description || ''
    ogImage = ogImage || seo.value?.og_image_url || ''
    ogImageAlt = ogImageAlt || seo.value?.og_image_alt_txt || ''
  } else {
    title = title || categoryInfo.value?.parent?.seo_title || ''
    description = description || categoryInfo.value?.parent?.meta_description || ''
    ogImage = ogImage || categoryInfo.value?.parent?.og_image_url || ''
    ogImageAlt = ogImageAlt || categoryInfo.value?.parent?.og_image_alt_txt || ''
  }
  title = title + seoTitleSuffix
  return {
    title,
    description,
    ogImage,
    ogImageAlt
  }
})
</script>

<template>
  <div>
    <Head>
      <title>{{ SeoMeta.title }}</title>
      <meta name="description" :content="SeoMeta.description" />
      <meta property="og:type" :content="categoryInfo.is_video ? 'video.other' : 'article'" />
      <meta property="og:image" :content="SeoMeta.ogImage" />
      <meta property="og:title" :content="SeoMeta.title" />
      <meta property="og:image:alt" :content="SeoMeta.ogImageAlt" />
      <meta property="og:url" :content="canonical" />
      <meta property="og:description" :content="SeoMeta.description" />
      <meta name="twitter:title" :content="SeoMeta.title" />
      <meta name="twitter:image" :content="SeoMeta.ogImage" />
      <meta name="twitter:image:alt" :content="SeoMeta.ogImageAlt" />
      <meta name="twitter:url" :content="canonical" />
      <meta property="twitter:description" :content="SeoMeta.description" />
    </Head>
    <NoticePage v-if="showNotice" />
    <div v-else-if="loading || failed" :class="{ 'error-page': failed }">
      <!-- v-if="loading"  -->
      <div class="tvb-loading-box"><n-spin /></div>
      <!-- <tvb-empty v-else icon="img_underdev" des="此頁面開發中，敬請期待"></tvb-empty> -->
    </div>
    <template v-else>
      <component :is="componentName"></component>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.error-page {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  .tvb-empty {
    height: 500px;
  }
  &.s,
  &.xs {
    align-items: stretch;
    padding-top: 100px !important;
    padding-bottom: 100px !important;
    .tvb-empty {
      height: 400px;
    }
  }
}
</style>
